import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Check, ArrowRight,
  Loader, AlertCircle, CreditCard,
  Calendar, ShieldCheck, Award, BarChart, Users, AlertTriangle,
  Tag, DollarSign
} from 'lucide-react';
import PlanConfirmation from '../components/modals/PlanConfirmation';
import PendingPlanChange from '../components/notifications/PendingPlanChange';
import axios from 'axios';
import MercadoPagoModal from '../components/modals/MercadoPagoModal';

// URL base para las peticiones API
const API_URL = 'https://backend-production-1786.up.railway.app/api';

// Crear cliente axios con interceptor
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Servicio de planes directo
const planServiceDirect = {
  async getPlanes() {
    try {
      const response = await apiClient.get('/suscripciones/planes');
      return response.data;
    } catch (error) {
      console.error('Error al obtener planes:', error);
      throw error;
    }
  },

  async getSuscripcionInfo() {
    try {
      const response = await apiClient.get('/suscripciones/info');
      return response.data;
    } catch (error) {
      console.error('Error al obtener información de suscripción:', error);
      throw error;
    }
  },

  async simularCambioPlan(planId, cicloFacturacion) {
    try {
      const response = await apiClient.post('/suscripciones/simular-cambio', {
        planId,
        cicloFacturacion
      });
      return response.data;
    } catch (error) {
      console.error('Error al simular cambio de plan:', error);
      throw error;
    }
  },

  async cambiarPlan(planId, cicloFacturacion, codigoVerificacion = null) {
    try {
      const payload = {
        planId,
        cicloFacturacion
      };

      if (codigoVerificacion) {
        payload.codigoVerificacion = codigoVerificacion;
      }

      const response = await apiClient.post('/suscripciones/cambiar-plan', payload);
      return response.data;
    } catch (error) {
      console.error('Error al cambiar plan:', error);
      throw error;
    }
  },

  async aplicarCambioGenerico(planId, cicloFacturacion, opciones = {}) {
    try {
      console.log('aplicarCambioGenerico directo llamado con:', {
        planId,
        cicloFacturacion,
        opciones
      });
  
      if (!planId) {
        throw new Error('El ID del plan es requerido');
      }
  
      if (!cicloFacturacion) {
        throw new Error('El ciclo de facturación es requerido');
      }
  
      const payload = {
        planId,
        cicloFacturacion,
        ...opciones
      };
  
      console.log('Payload construido:', payload);
  
      // LÓGICA MEJORADA DE SELECCIÓN DE ENDPOINT
      let endpoint = '/suscripciones/cambiar-plan';
      let razon = 'Cambio estándar';

      // 1. Verificar si es una transacción gratuita por varios criterios
      // FIX: Convertir transactionId a string antes de usar startsWith
      const transactionIdStr = opciones.transactionId ? String(opciones.transactionId) : '';

      const esTransaccionGratuita = transactionIdStr.startsWith('free-');
      const esPrecioGratuito = opciones.montoFinal !== undefined && opciones.montoFinal <= 0.01;
      const tieneDescuentos = opciones.cuponId || (opciones.saldoUtilizado && opciones.saldoUtilizado > 0);
      const metodoPagoGratuito = opciones.metodoPago === 'gratuito';

      // 2. Si es definitivamente una transacción de PayU (con pago real)
      // MEJORADO: Detectar PayU por el método de pago y que no sea gratuito
      if (opciones.metodoPago === 'payu' && 
          opciones.transactionId && 
          !esTransaccionGratuita && 
          !metodoPagoGratuito &&
          (opciones.montoFinal === undefined || opciones.montoFinal > 0.01)) {
        endpoint = '/suscripciones/aplicar-pago-payu';
        razon = 'Pago real con PayU';
      } 
      // 3. Si es gratuito por cualquier razón (cupón, saldo, o precio 0)
      else if (esTransaccionGratuita || esPrecioGratuito || metodoPagoGratuito || 
              (tieneDescuentos && opciones.montoFinal <= 0.01)) {
        endpoint = '/suscripciones/aplicar-compra-saldo';
        razon = `Transacción gratuita - ${esTransaccionGratuita ? 'ID gratuito' : ''} ${esPrecioGratuito ? 'Precio 0' : ''} ${metodoPagoGratuito ? 'Método gratuito' : ''} ${tieneDescuentos ? 'Con descuentos' : ''}`.trim();
      }

      console.log('Usando endpoint:', endpoint);
      console.log('Razón de selección:', razon);
      console.log('Análisis detallado:', {
        tieneTransactionId: !!opciones.transactionId,
        transactionIdOriginal: opciones.transactionId,
        transactionIdStr,
        esTransaccionGratuita,
        esPrecioGratuito,
        tieneDescuentos,
        metodoPagoGratuito,
        metodoPago: opciones.metodoPago,
        montoFinal: opciones.montoFinal,
        cuponId: opciones.cuponId,
        saldoUtilizado: opciones.saldoUtilizado
      });
  
      const response = await apiClient.post(endpoint, payload);
      console.log('Respuesta del servidor:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error en aplicarCambioGenerico directo:', error);
      
      // Log adicional para debug
      if (error.response) {
        console.error('Error del servidor:', error.response.data);
        console.error('Status:', error.response.status);
      }
      
      throw error;
    }
  },

  async aplicarCompraSaldo(planId, cicloFacturacion, cuponId = null, saldoUtilizado = 0, montoOriginal = 0, montoFinal = 0, codigoVerificacion = null) {
    try {
      console.log('aplicarCompraSaldo directo llamado con:', {
        planId,
        cicloFacturacion,
        cuponId,
        saldoUtilizado,
        montoOriginal,
        montoFinal
      });

      if (!planId) {
        throw new Error('El ID del plan es requerido');
      }

      if (!cicloFacturacion) {
        throw new Error('El ciclo de facturación es requerido');
      }

      if (montoFinal > 0.1) {
        throw new Error('Este método solo es para planes gratuitos (precio final = 0)');
      }

      const payload = {
        planId,
        cicloFacturacion,
        cuponId,
        saldoUtilizado,
        montoOriginal,
        montoFinal: 0
      };

      if (codigoVerificacion) {
        payload.codigoVerificacion = codigoVerificacion;
      }

      const response = await apiClient.post('/suscripciones/aplicar-compra-saldo', payload);
      return response.data;
    } catch (error) {
      console.error('Error al aplicar compra con saldo/cupón:', error);
      throw error;
    }
  },

  async cancelarCambioPendiente(cambioId) {
    try {
      const response = await apiClient.post('/suscripciones/cancelar-cambio', {
        cambioId
      });
      return response.data;
    } catch (error) {
      console.error('Error al cancelar cambio pendiente:', error);
      throw error;
    }
  }
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const UpgradePlan = () => {
  console.log('Renderizando componente UpgradePlan');

  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [error, setError] = useState(null);
  const [billingCycle, setBillingCycle] = useState('mensual');
  const [availablePlans, setAvailablePlans] = useState([]);
  const [suscripcionInfo, setSuscripcionInfo] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [simulacion, setSimulacion] = useState(null);
  const [simularLoading, setSimularLoading] = useState(false);

  // Estados para PayU
  const [showPayuModal, setShowPayuModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  // Estados para Cupones y Saldo
  const [cuponAplicado, setCuponAplicado] = useState(null);
  const [saldoAplicado, setSaldoAplicado] = useState(0);
  const [precioOriginal, setPrecioOriginal] = useState(0);
  const [precioFinal, setPrecioFinal] = useState(0);

  // Log para verificar datos del usuario
  useEffect(() => {
    console.log('Usuario actual:', user);
    console.log('Tipo de usuario:', user?.tipo_usuario_id);
  }, [user]);

  // Log para verificar cambios en planes disponibles
  useEffect(() => {
    console.log('Planes disponibles actualizados:', availablePlans);
  }, [availablePlans]);

  // Cargar información necesaria al inicio
  useEffect(() => {
    const fetchData = async () => {
      console.log('Iniciando fetchData...');
      try {
        setLoading(true);
        setError(null);

        console.log('Obteniendo información de suscripción...');
        // 1. Obtener información de la suscripción actual
        const suscripcionResponse = await planServiceDirect.getSuscripcionInfo();
        console.log('Respuesta de suscripción:', suscripcionResponse);

        setSuscripcionInfo(suscripcionResponse.suscripcion);

        if (suscripcionResponse.suscripcion) {
          console.log('Plan actual según suscripción:', suscripcionResponse.suscripcion.plan_codigo);
          setCurrentPlan(suscripcionResponse.suscripcion.plan_codigo);

          // Si hay un ciclo configurado, usarlo como predeterminado
          if (suscripcionResponse.suscripcion.ciclo_facturacion) {
            console.log('Ciclo de facturación encontrado:', suscripcionResponse.suscripcion.ciclo_facturacion);
            setBillingCycle(suscripcionResponse.suscripcion.ciclo_facturacion);
          }
        }

        console.log('Obteniendo planes disponibles...');
        // 2. Obtener planes disponibles
        const planesResponse = await planServiceDirect.getPlanes();
        console.log('Respuesta de planes:', planesResponse);

        if (!planesResponse.planes || planesResponse.planes.length === 0) {
          console.warn('No se encontraron planes disponibles en la respuesta');
        }

        // Transformar los planes al formato que espera el componente
        const formattedPlans = formatPlans(planesResponse.planes || []);
        console.log('Planes formateados:', formattedPlans);

        setAvailablePlans(formattedPlans);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError(error.message || 'Error al cargar la información de planes');
      } finally {
        setLoading(false);
        console.log('Carga de datos finalizada, loading =', false);
      }
    };

    fetchData();
  }, []);

  // Función para formatear los planes según el formato esperado por el UI
  const formatPlans = (planes) => {
    console.log('Iniciando formateo de planes. Planes recibidos:', planes);
    console.log('Usuario:', user);

    if (!planes || !planes.length || !user) {
      console.warn('No hay planes para formatear o no hay usuario');
      return [];
    }

    // Iconos disponibles para asignar a los planes
    const iconMap = {
      'gratuito': CreditCard,
      'casual': Calendar,
      'socio_inmobiliaria': Building2,
      'basico': ShieldCheck,
      'estandar': BarChart,
      'premium': Award,
      'premium_constructora': Award
    };

    // Colores para los planes
    const colorMap = {
      'gratuito': {
        color: 'from-gray-300 to-gray-400',
        textColor: 'text-gray-700',
        gradientButton: 'from-gray-500 to-gray-600'
      },
      'casual': {
        color: 'from-indigo-500 to-blue-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-indigo-600 to-blue-600',
        featured: true
      },
      'socio_inmobiliaria': {
        color: 'from-green-500 to-teal-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-green-600 to-teal-600',
        specialRoute: '/upgrade-to-inmobiliaria'
      },
      'basico': {
        color: 'from-green-500 to-teal-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-green-600 to-teal-600'
      },
      'estandar': {
        color: 'from-indigo-500 to-blue-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-indigo-600 to-blue-600',
        featured: true
      },
      'premium': {
        color: 'from-amber-500 to-yellow-500',
        textColor: 'text-gray-800',
        gradientButton: 'from-amber-600 to-yellow-600'
      },
      'premium_constructora': {
        color: 'from-purple-500 to-pink-500',
        textColor: 'text-gray-800',
        gradientButton: 'from-purple-600 to-pink-600',
        featured: true
      }
    };

    // Convertir las características en un array para el UI
    const getFeatures = (description) => {
      if (!description) return [];
      return description.split(', ');
    };

    const formattedPlans = planes.map(plan => {
      console.log('Formateando plan:', plan);
      const colorConfig = colorMap[plan.codigo] || colorMap.basico;

      return {
        id: plan.id,
        dbId: plan.id, // Guardar el ID real de la base de datos
        name: plan.nombre,
        codigo: plan.codigo,
        price: billingCycle === 'mensual' ? plan.precio_mensual.toString() : plan.precio_anual.toString(),
        properties: plan.propiedades_max || 'Ilimitadas',
        color: colorConfig.color,
        textColor: colorConfig.textColor,
        gradientButton: colorConfig.gradientButton,
        icon: iconMap[plan.codigo] || CreditCard,
        benefits: getFeatures(plan.descripcion),
        featured: colorConfig.featured || false,
        specialRoute: colorConfig.specialRoute || null
      };
    });

    console.log('Planes formateados, resultado:', formattedPlans);
    return formattedPlans;
  };

  // Simular cambio de plan
  const simularCambio = async (planId) => {
    try {
      setSimularLoading(true);
      setError(null);

      // Verificar el ID del plan
      if (!planId || isNaN(parseInt(planId))) {
        throw new Error('ID de plan inválido');
      }

      console.log('Enviando simulación con planId:', planId, 'ciclo:', billingCycle);

      const response = await planServiceDirect.simularCambioPlan(planId, billingCycle);
      setSimulacion(response.simulacion);

      return response.simulacion;
    } catch (error) {
      console.error('Error al simular cambio:', error);
      // Mensaje de error más descriptivo
      const errorMsg = error.message || 'Error al simular el cambio de plan';
      setError(errorMsg);
      return null;
    } finally {
      setSimularLoading(false);
    }
  };

  // Preparar el proceso de pago con PayU
  const handlePayment = async (planId) => {
    try {
      // Limpiar errores previos
      setError(null);
      setPaymentError(null);

      // Buscar el plan seleccionado
      const plan = availablePlans.find(p => p.id === planId);

      if (!plan) {
        throw new Error('Plan no válido para este tipo de usuario');
      }

      // Si el plan tiene una ruta especial, navegar directamente
      if (plan.specialRoute) {
        navigate(plan.specialRoute);
        return;
      }

      // Establecer el plan seleccionado
      setSelectedPlan(plan);

      // Para planes gratuitos, manejar sin pago
      if (parseFloat(plan.price) === 0) {
        handleFreeUpgrade(plan.dbId);
        return;
      }

      // Calcular el precio final considerando cupones y saldo
      const precio = parseFloat(plan.price);
      setPrecioOriginal(precio);
      let precioAjustado = precio;

      // Aplicar descuento de cupón si existe
      if (cuponAplicado) {
        precioAjustado = cuponAplicado.descuento.montoFinal;
      }

      // Aplicar saldo si se ha seleccionado
      if (saldoAplicado > 0) {
        precioAjustado = Math.max(0, precioAjustado - saldoAplicado);
      }

      setPrecioFinal(precioAjustado);

      // Si el precio final es 0 (cubierto por cupón o saldo), procesar directamente
      if (precioAjustado <= 0) {
        // Aquí manejaríamos el caso de compra con saldo o cupón que cubre todo
        handleFreeUpgrade(plan.dbId, true);
        return;
      }

      // Mostrar modal de PayU para planes pagados
      setShowPayuModal(true);
    } catch (error) {
      console.error('Error al preparar el pago:', error);
      setError(error.message || 'Error al preparar el pago');
    }
  };

  // Manejar actualizaciones a planes gratuitos o pagados con saldo/cupón
  const handleFreeUpgrade = async (planId, usaSaldoOCupon = false) => {
    try {
      setLoading(true);
      setError(null);

      let result;

      if (usaSaldoOCupon) {
        // Usar aplicarCompraSaldo para planes cubiertos por cupón/saldo
        result = await planServiceDirect.aplicarCompraSaldo(
          planId,
          billingCycle,
          cuponAplicado ? cuponAplicado.cupon.id : null,
          saldoAplicado,
          precioOriginal,
          0, // montoFinal = 0
          null // codigoVerificacion
        );
      } else {
        // Usar cambiarPlan para planes gratuitos
        result = await planServiceDirect.cambiarPlan(planId, billingCycle);
      }

      // Manejar el resultado
      handleUpgradeSuccess(result);

      return result;
    } catch (error) {
      console.error('Error al cambiar al plan:', error);
      setError(error.message || 'Error al cambiar al plan');
    } finally {
      setLoading(false);
    }
  };

  // Manejar el éxito de un pago con PayU
  const handlePaymentSuccess = async (paymentResult) => {
    try {
      console.log('Pago exitoso con PayU:', paymentResult);
      setPaymentSuccess(paymentResult);

      // Simular cambio de plan después del pago exitoso
      if (selectedPlan) {
        const simulacionResult = await simularCambio(selectedPlan.dbId);

        if (!simulacionResult) {
          throw new Error('Error al simular el cambio de plan');
        }

        // Mostrar modal de confirmación de cambio
        setShowConfirmModal(true);
      }

      // Cerrar el modal de pago
      setTimeout(() => {
        setShowPayuModal(false);
      }, 1000);

    } catch (error) {
      console.error('Error después del pago exitoso:', error);
      setPaymentError(error.message || 'Error al procesar el cambio de plan');
    }
  };

  // Manejar el error de un pago con PayU
  const handlePaymentError = (error) => {
    console.error('Error en el pago con PayU:', error);
    setPaymentError(error.message || 'Error durante el proceso de pago');
  };

  // Manejar la selección de un plan
  const handleUpgrade = async (planId) => {
    try {
      setLoading(true);
      setError(null);

      // Buscar el plan seleccionado
      const plan = availablePlans.find(p => p.id === planId);

      if (!plan) {
        throw new Error('Plan no válido para este tipo de usuario');
      }

      // Si tiene una ruta especial, navegar directamente
      if (plan.specialRoute) {
        navigate(plan.specialRoute);
        return;
      }

      // Iniciar proceso de pago
      handlePayment(planId);
    } catch (error) {
      console.error('Error al seleccionar plan:', error);
      setError(error.message || 'Error al seleccionar el plan');
    } finally {
      setLoading(false);
    }
  };

  // Confirmar el cambio de plan
  const handleConfirmUpgrade = async (verificationCode = null, isFreePlan = false) => {
    console.log('Confirmando upgrade con código de verificación:', verificationCode || 'ninguno');
    console.log('Es un plan gratuito o con cupón que cubre todo:', isFreePlan);
    console.log('PaymentSuccess data:', paymentSuccess);

    try {
      if (!selectedPlan || !selectedPlan.dbId) {
        throw new Error('No hay plan seleccionado o falta el ID del plan');
      }

      const planId = selectedPlan.dbId;
      console.log('Plan ID a usar:', planId);

      if (!planId || isNaN(parseInt(planId))) {
        throw new Error(`ID de plan inválido: ${planId}`);
      }

      // USAR DATOS DEL PAYMENT SUCCESS SI ESTÁN DISPONIBLES
      let montoOriginalFinal = parseFloat(selectedPlan.price) || 0;
      let montoFinalCalculado = montoOriginalFinal;
      let cuponInfo = null;
      let saldoInfo = 0;

      // Si hay datos del pago exitoso, usarlos (tienen prioridad)
      if (paymentSuccess && paymentSuccess.isFree) {
        console.log('Usando datos del payment success');
        montoOriginalFinal = paymentSuccess.precioOriginal || montoOriginalFinal;
        montoFinalCalculado = paymentSuccess.precioFinal || 0;
        cuponInfo = paymentSuccess.cuponAplicado;
        saldoInfo = paymentSuccess.saldoAplicado || 0;
      } else {
        // Fallback: calcular desde los estados locales
        console.log('Calculando desde estados locales');
        
        // Aplicar descuento de cupón si existe
        if (cuponAplicado && cuponAplicado.descuento) {
          montoFinalCalculado = cuponAplicado.descuento.montoFinal || 0;
          cuponInfo = cuponAplicado;
        }
        
        // Aplicar descuento de saldo si existe
        if (saldoAplicado > 0) {
          montoFinalCalculado = Math.max(0, montoFinalCalculado - saldoAplicado);
          saldoInfo = saldoAplicado;
        }
      }
      
      console.log('Precios calculados:', {
        montoOriginalFinal,
        montoFinalCalculado,
        cuponInfo: cuponInfo ? { id: cuponInfo.cupon?.id, descuento: cuponInfo.descuento?.montoDescuento } : null,
        saldoInfo
      });

      // Conjunto de opciones según el contexto
      const opciones = {
        codigoVerificacion: verificationCode || null,
        montoOriginal: montoOriginalFinal,
        montoFinal: montoFinalCalculado
      };

      // Verificar si es una transacción gratuita
      // FIX: Convertir transactionId a string antes de usar startsWith
      const transactionIdStr = paymentSuccess?.transactionId ? String(paymentSuccess.transactionId) : '';
      
      const esTransaccionGratuita = paymentSuccess?.isFree || 
                                    transactionIdStr.startsWith('free-') ||
                                    montoFinalCalculado <= 0.01;
      
      console.log('Análisis de transacción:', {
        esTransaccionGratuita,
        paymentSuccessIsFree: paymentSuccess?.isFree,
        transactionIdStartsWithFree: transactionIdStr.startsWith('free-'),
        transactionIdStr,
        originalTransactionId: paymentSuccess?.transactionId,
        montoFinalCalculado
      });

      // Si hay cupón aplicado (desde paymentSuccess o estado local)
      if (cuponInfo && cuponInfo.cupon) {
        opciones.cuponId = cuponInfo.cupon.id;
      }

      // Si hay saldo aplicado
      if (saldoInfo > 0) {
        opciones.saldoUtilizado = saldoInfo;
      }

      // Si hay un pago con PayU REAL (no gratuito)
      if (paymentSuccess && paymentSuccess.transactionId && !esTransaccionGratuita) {
        // FIX PRINCIPAL: Usar el transactionId interno (número) en lugar del PayU ID
        // Si paymentSuccess.transactionId es un número, usar ese directamente
        if (typeof paymentSuccess.transactionId === 'number') {
          opciones.transactionId = paymentSuccess.transactionId; // Usar ID interno (12)
          console.log('Usando transactionId interno para buscar transacción:', paymentSuccess.transactionId);
        } 
        // Si hay un campo específico para transaccion_id_payu, usar ese
        else if (paymentSuccess.transaccionIdPayu) {
          opciones.transactionId = paymentSuccess.transaccionIdPayu;
          console.log('Usando transaccionIdPayu:', paymentSuccess.transaccionIdPayu);
        }
        // Fallback al payuTransactionId (aunque puede ser orderId)
        else {
          opciones.transactionId = paymentSuccess.payuTransactionId || paymentSuccess.transactionId;
          console.log('Usando payuTransactionId como fallback:', paymentSuccess.payuTransactionId);
        }
        
        opciones.metodoPago = paymentSuccess.metodoPago || 'payu';
        
        console.log('Configurando para pago PayU:', {
          transactionIdInterno: paymentSuccess.transactionId,
          payuTransactionId: paymentSuccess.payuTransactionId,
          transactionIdEnviado: opciones.transactionId,
          tipoTransactionIdEnviado: typeof opciones.transactionId,
          paymentSuccessCompleto: paymentSuccess
        });
        
        // Validación adicional
        if (!opciones.transactionId) {
          console.error('Transaction ID no encontrado:', paymentSuccess);
          throw new Error('Transaction ID es requerido para procesar el pago');
        }
      } else if (esTransaccionGratuita) {
        // Para transacciones gratuitas
        opciones.transactionId = paymentSuccess?.transactionId || `free-${Date.now()}`;
        opciones.metodoPago = 'gratuito';
      }

      console.log('Opciones finales para cambio de plan:', opciones);

      // Usar el servicio directo
      const result = await planServiceDirect.aplicarCambioGenerico(
        planId,
        billingCycle,
        opciones
      );

      console.log('Resultado del cambio de plan:', result);
      return result;
    } catch (error) {
      console.error('Error detallado al confirmar cambio:', error);
      throw error;
    }
  };

    // Manejar la cancelación de un cambio pendiente
    const handleCancelPendingChange = async (cambioId) => {
      console.log('Cancelando cambio pendiente ID:', cambioId);
      try {
        const result = await planServiceDirect.cancelarCambioPendiente(cambioId);
        console.log('Resultado de cancelación:', result);

        // Actualizar la información de suscripción
        const suscripcionResponse = await planServiceDirect.getSuscripcionInfo();
        setSuscripcionInfo(suscripcionResponse.suscripcion);

        return result;
      } catch (error) {
        console.error('Error al cancelar cambio pendiente:', error);
        throw error;
      }
    };

  // Manejar el éxito de cambio de plan
  const handleUpgradeSuccess = (result) => {
    console.log('Cambio de plan exitoso:', result);

    // Cerrar los modales
    setShowConfirmModal(false);
    setShowPayuModal(false);

    // Actualizar el estado local
    if (result.plan && result.plan.codigo) {
      console.log('Actualizando plan actual a:', result.plan.codigo);
      setCurrentPlan(result.plan.codigo);
    }

    // Reiniciar estados de cupón y saldo
    setCuponAplicado(null);
    setSaldoAplicado(0);

    // Si es cambio inmediato, actualizar usuario en localStorage
    if (result.cambio_inmediato) {
      console.log('Cambio inmediato, actualizando localStorage');
      const updatedUser = {
        ...JSON.parse(localStorage.getItem('user')),
        plan: result.plan.codigo,
        fecha_proximo_pago: result.plan.fecha_fin
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Recargar la página para aplicar cambios
      console.log('Redirigiendo a /mis_propiedades en 1 segundo...');
      setTimeout(() => {
        window.location.href = '/mis_propiedades';
      }, 1000);
    } else {
      // Actualizar información de suscripción
      console.log('Actualizando información de suscripción tras cambio programado');
      planServiceDirect.getSuscripcionInfo()
        .then(response => {
          setSuscripcionInfo(response.suscripcion);
        })
        .catch(err => console.error('Error al actualizar info:', err));
    }
  };

  // Calcular precio anual (10 meses) y ahorro (2 meses)
  const calculateAnnualPrice = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 10).toFixed(2);
  };

  const calculateSavings = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 2).toFixed(2);
  };

  // Gestionar la aplicación de un cupón
  const handleCuponAplicado = (resultadoCupon) => {
    setCuponAplicado(resultadoCupon);

    // Si tenemos un plan seleccionado, actualizar el precio final
    if (selectedPlan) {
      const precioOriginal = parseFloat(selectedPlan.price);
      setPrecioOriginal(precioOriginal);
      setPrecioFinal(resultadoCupon.descuento.montoFinal);
    }
  };

  // Gestionar la eliminación de un cupón
  const handleRemoveCupon = () => {
    setCuponAplicado(null);

    // Restaurar precio original si hay un plan seleccionado
    if (selectedPlan) {
      const precioOriginal = parseFloat(selectedPlan.price);
      setPrecioOriginal(precioOriginal);
      setPrecioFinal(precioOriginal - saldoAplicado);
    }
  };

  // Gestionar el uso de saldo
  const handleUseSaldo = (monto) => {
    setSaldoAplicado(monto);

    // Actualizar precio final si hay un plan seleccionado
    if (selectedPlan) {
      const precioBase = cuponAplicado
        ? cuponAplicado.descuento.montoFinal
        : parseFloat(selectedPlan.price);

      setPrecioFinal(Math.max(0, precioBase - monto));
    }
  };

  // Planes de prueba estáticos para fallback
  const staticTestPlans = [
    {
      id: "1",
      dbId: 1,
      name: 'Gratuito',
      codigo: 'gratuito',
      price: "0.00",
      properties: 1,
      color: 'from-gray-300 to-gray-400',
      textColor: 'text-gray-700',
      gradientButton: 'from-gray-500 to-gray-600',
      icon: CreditCard,
      benefits: ['Primera publicación gratis', 'Perfil básico', 'Estadísticas básicas', 'Sin permanencia'],
      featured: false
    },
    {
      id: "2",
      dbId: 2,
      name: 'Casual',
      codigo: 'casual',
      price: "99.90",
      properties: 5,
      color: 'from-indigo-500 to-blue-600',
      textColor: 'text-gray-800',
      gradientButton: 'from-indigo-600 to-blue-600',
      icon: Calendar,
      benefits: ['Hasta 5 publicaciones', 'Perfil profesional', 'Estadísticas detalladas', 'Soporte prioritario', 'Herramientas de promoción'],
      featured: true
    },
    {
      id: "3",
      dbId: 3,
      name: 'Socio Inmobiliaria',
      codigo: 'socio_inmobiliaria',
      price: "199.90",
      properties: 10,
      color: 'from-green-500 to-teal-600',
      textColor: 'text-gray-800',
      gradientButton: 'from-green-600 to-teal-600',
      icon: Building2,
      benefits: ['Hasta 10 publicaciones', 'Perfil empresarial', 'Panel avanzado', 'Destaca tus Propiedades', 'Soporte Premium'],
      specialRoute: '/upgrade-to-inmobiliaria'
    }
  ];

 // Comprobar si necesitamos usar planes de prueba estáticos
 if (availablePlans.length === 0 && !loading) {
  console.log('No hay planes disponibles, usando planes estáticos de prueba');

  // Filtrar planes según tipo de usuario
  let testPlans = [];

  if (user?.tipo_usuario_id === 2) {
    // Vendedor individual
    testPlans = staticTestPlans;
  } else if (user?.tipo_usuario_id === 4) {
    // Inmobiliaria
    testPlans = [
      {
        id: "4",
        dbId: 4,
        name: 'Básico',
        codigo: 'basico',
        price: "199.90",
        properties: 10,
        color: 'from-green-500 to-teal-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-green-600 to-teal-600',
        icon: ShieldCheck,
        benefits: ['Hasta 10 publicaciones', 'Perfil empresarial', 'Estadísticas básicas', 'Soporte por email'],
        featured: false
      },
      {
        id: "5",
        dbId: 5,
        name: 'Estándar',
        codigo: 'estandar',
        price: "349.90",
        properties: 20,
        color: 'from-indigo-500 to-blue-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-indigo-600 to-blue-600',
        icon: BarChart,
        benefits: ['Hasta 20 publicaciones', 'Perfil empresarial destacado', 'Estadísticas avanzadas', 'Soporte prioritario', 'Logo en propiedades'],
        featured: true
      },
      {
        id: "6",
        dbId: 6,
        name: 'Premium',
        codigo: 'premium',
        price: "499.90",
        properties: 'Ilimitadas',
        color: 'from-amber-500 to-yellow-500',
        textColor: 'text-gray-800',
        gradientButton: 'from-amber-600 to-yellow-600',
        icon: Award,
        benefits: ['Publicaciones ilimitadas', 'Perfil empresarial premium', 'Estadísticas en tiempo real', 'Soporte 24/7', 'Logo destacado'],
        featured: false
      }
    ];
  } else if (user?.tipo_usuario_id === 3) {
    // Constructora
    testPlans = [
      {
        id: "7",
        dbId: 7,
        name: 'Premium Constructora',
        codigo: 'premium_constructora',
        price: "699.90",
        properties: 'Ilimitadas',
        color: 'from-purple-500 to-pink-500',
        textColor: 'text-gray-800',
        gradientButton: 'from-purple-600 to-pink-600',
        icon: Award,
        benefits: ['Publicaciones ilimitadas', 'Perfil empresarial premium', 'Estadísticas en tiempo real', 'Soporte 24/7', 'Logo destacado', 'Acceso a herramientas de análisis inmobiliario'],
        featured: true
      }
    ];
  }

  setAvailablePlans(testPlans);
}

if (loading) {
  console.log('Mostrando pantalla de carga');
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-white pt-32">
      <div className="flex flex-col items-center">
        <motion.div
          className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <p className="mt-4 text-indigo-700 font-medium">Cargando planes...</p>
      </div>
    </div>
  );
}

// Si es constructora, redirigir
if (user?.tipo_usuario_id === 3) {
  console.log('Usuario es constructora, redirigiendo a perfil');
  navigate('/mi_perfil');
  return null;
}

console.log('Renderizando planes. Disponibles:', availablePlans.length);
console.log('Plan actual:', currentPlan);

return (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-16 px-4">
    <motion.div
      className="max-w-6xl mx-auto"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.div variants={fadeInUp} className="text-center mb-10">
        <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
          Elige el Plan Perfecto para Ti
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          {user?.tipo_usuario_id === 2
            ? 'Impulsa tus ventas con nuestros planes para vendedores'
            : 'Haz crecer tu inmobiliaria con nuestros planes empresariales'}
        </p>
      </motion.div>

      {/* Mostrar notificación de cambio pendiente si existe */}
      {suscripcionInfo && suscripcionInfo.cambio_pendiente && (
        <motion.div variants={fadeInUp} className="mb-8">
          <PendingPlanChange
            cambio={suscripcionInfo.cambio_pendiente}
            onCancel={handleCancelPendingChange}
            onDismiss={() => {}} // Opcional: manejar dismissal temporal
          />
        </motion.div>
      )}

      {/* Billing cycle toggle */}
      <motion.div variants={fadeInUp} className="flex justify-center items-center mb-8">
        <div className="bg-white p-2 rounded-xl shadow-md">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setBillingCycle('mensual')}
              className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                billingCycle === 'mensual'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('anual')}
              className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                billingCycle === 'anual'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Anual (2 meses gratis)
            </button>
          </div>
        </div>
      </motion.div>

      {error && (
        <motion.div
          variants={fadeInUp}
          className="max-w-md mx-auto mb-8 bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-2 shadow-lg"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </motion.div>
      )}

      {/* Verificador de planes vacíos */}
      {availablePlans.length === 0 && (
        <motion.div
          variants={fadeInUp}
          className="max-w-md mx-auto mb-8 bg-yellow-50 text-yellow-600 p-4 rounded-xl flex items-center gap-2 shadow-lg"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p>No se encontraron planes disponibles para tu tipo de usuario ({user?.tipo_usuario_id}).</p>
        </motion.div>
      )}

      {/* Sección de cupón y saldo - Solo se muestra cuando hay un plan seleccionado */}
      {selectedPlan && (
        <motion.div
          variants={fadeInUp}
          className="max-w-2xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"
        >

          {/* Resumen de pago */}
          {(cuponAplicado || saldoAplicado > 0) && (
            <div className="md:col-span-2 bg-white p-4 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-indigo-600" />
                Resumen de pago
              </h3>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Precio original:</span>
                  <span>S/ {parseFloat(selectedPlan.price).toFixed(2)}</span>
                </div>

                {cuponAplicado && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Descuento por cupón:</span>
                    <span>- S/ {cuponAplicado.descuento.montoDescuento.toFixed(2)}</span>
                  </div>
                )}

                {saldoAplicado > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Saldo utilizado:</span>
                    <span>- S/ {saldoAplicado.toFixed(2)}</span>
                  </div>
                )}

                <div className="border-t pt-2 flex justify-between items-center font-bold">
                  <span className="text-gray-800">Total a pagar:</span>
                  <span className="text-xl text-indigo-700">
                    S/ {precioFinal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        variants={staggerContainer}
      >
        {availablePlans.map((plan) => {
          console.log('Renderizando plan:', plan.name);
          const Icon = plan.icon;
          const monthlyPrice = plan.price;
          const annualPrice = calculateAnnualPrice(monthlyPrice);
          const savingsAmount = calculateSavings(monthlyPrice);

          return (
            <motion.div
              key={plan.id}
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden
                transition-all duration-300
                ${plan.featured ? 'lg:-mt-4 lg:mb-4 border-2 border-blue-500' : ''}
                ${selectedPlan?.id === plan.id ? 'ring-2 ring-indigo-500' : ''}
              `}
            >
              <div className="absolute top-4 right-4 z-10 flex space-x-2">
                {currentPlan === plan.codigo && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    Plan Actual
                  </span>
                )}
                {plan.featured && (
                  <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Recomendado
                  </span>
                )}
              </div>

              <div
                className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-10`}
              />

              <div className="relative p-8 z-10">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-full bg-gradient-to-br ${plan.color} text-white mr-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`text-2xl font-bold ${plan.textColor}`}>
                    {plan.name}
                  </h3>
                </div>

                {plan.codigo === 'socio_inmobiliaria' && (
                  <div className="text-sm text-gray-500 mb-2">Desde</div>
                )}

                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold text-gray-800">
                    S/ {billingCycle === 'mensual' ? monthlyPrice : annualPrice}
                  </span>
                  <span className="text-gray-500 ml-2">
                    /{billingCycle === 'mensual' ? 'mes' : 'año'}
                  </span>
                </div>

                {billingCycle === 'anual' && monthlyPrice !== '0' && (
                  <div className="mb-6 text-sm text-green-600 font-medium">
                    Ahorras S/ {savingsAmount} al año
                  </div>
                )}
                {billingCycle === 'mensual' && (
                  <div className="mb-6 h-5"></div> // Espacio para mantener alineación
                )}

                <div className="border-t border-gray-100 pt-6 mb-8">
                  <p className="text-sm text-gray-600 mb-4">
                    {typeof plan.properties === 'number'
                      ? `Hasta ${plan.properties} publicaciones`
                      : 'Publicaciones ilimitadas'}
                  </p>

                  <ul className="space-y-4">
                    {plan.benefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-gray-700"
                      >
                        <div className="flex-shrink-0 rounded-full p-1 bg-green-100">
                          <Check className="w-4 h-4 text-green-500" />
                        </div>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <motion.button
                  onClick={() => {
                    setSelectedPlan(plan);
                    handleUpgrade(plan.id);
                  }}
                  disabled={loading || currentPlan === plan.codigo || simularLoading}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-md
                    ${currentPlan === plan.codigo
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : loading || simularLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : `bg-gradient-to-r ${plan.gradientButton} text-white hover:shadow-lg`
                    } transition-all duration-300`}
                >
                  {currentPlan === plan.codigo ? (
                    'Plan Actual'
                  ) : loading || simularLoading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Cargando...
                    </>
                  ) : (
                    <>
                      Seleccionar Plan
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>

    {/* Modal de confirmación */}
    <PlanConfirmation
      isOpen={showConfirmModal}
      onClose={() => setShowConfirmModal(false)}
      simulacion={simulacion}
      onConfirm={handleConfirmUpgrade}
      onSuccess={handleUpgradeSuccess}
      // Pasar información de precio para determinar si es gratuito
      precioFinal={precioFinal}
      cuponAplicado={cuponAplicado}
      saldoAplicado={saldoAplicado}
      precioOriginal={precioOriginal}
      selectedPlan={selectedPlan}
    />

    {/* Modal de pago con PayU */}
    <MercadoPagoModal
      isOpen={showPayuModal}
      onClose={() => setShowPayuModal(false)}
      planInfo={selectedPlan}
      billingCycle={billingCycle}
      onPaymentSuccess={handlePaymentSuccess}
      onPaymentError={handlePaymentError}
      // Pasar info de descuentos
      cuponAplicado={cuponAplicado}
      saldoAplicado={saldoAplicado}
      precioOriginal={precioOriginal}
      precioFinal={precioFinal}
    />
  </div>
);
};

export default UpgradePlan;